// ResourceTabs — the author's working memory, pure. Composes IconButton +
// VisuallyHidden over the shared Navigation vocabulary.
//
// The strip renders a `TabSet` (Navigation/types.js): the resources the author
// has OPEN within the workspace — the working set, never the whole flat pool
// (the left rail is the explorer; this row is what's in hand).
//
// There is NO pinned home chip (owner decision 2026-08-24) and NO separate hero
// chip (hero-is-initial-conversation-state, 2026-08-31): the landing / new-tab
// surface is now the EMPTY state of a CONVERSATION tab. Every "+" / Cmd+T opens
// another nascent conversation (multiple allowed), and closing the last tab of
// any kind seeds a fresh one. The strip order is the open resources, then the
// preview glance, the quality reports, the conversations (nascent or durable),
// and the trailing settings singleton.
//
// A11y shape — plain buttons with `aria-current="page"`, NOT role="tablist":
// the ARIA tabs pattern promises a tabpanel this component does not own (the
// "panel" is the AppShell centre view, controlled elsewhere), and each resource
// tab carries a nested close button, which a composite `tab` role cannot host
// without an interactive control inside an interactive control (axe
// `nested-interactive`, at ERROR here). Activating a tab is navigation — the
// centre view changes to another resource — so a <nav> landmark of ordinary
// buttons, the active one marked `aria-current="page"`, is the honest,
// bulletproof semantic: every control is natively focusable and Tab/Shift-Tab
// operable, no hand-rolled roving tabindex to get wrong.
//
// The component only REPORTS intent: `onActivate` with the shared `ActiveTab`
// sum, `onClose` with the tab's `ResourceId`. It never runs the folds — the
// host applies `activateTab`/`closeTab` and, when `closeTab` reports
// `unsaved`, mounts `ConfirmDiscard` (sibling file) inside a Dialog. Closing
// never deletes the resource; `dirty` must be visible in the strip, so it is
// carried by a dot AND hidden text ("unsaved changes"), never colour alone.
//
// Register (v3, IDD d5f68ea8): PURE figure-ground. The strip is recessed (the
// same plane as the top strip) and carries no hairline, so omnibar + tab strip
// read as one continuous top surface. A resting inactive tab has no shape and no
// fill — just its dimmed label, delineated from a neighbour by a thin 1px "|"
// separator (dropped beside the active/hovered tab). The active tab is filled
// with the CONTENT plane (--color-bg-subtle) and merges into the pane below (open
// bottom) — that colour is the ENTIRE signal: no accent, no border, no line.
// Hover is a rounded-rectangle pill (sunken chip), distinct from the active
// open-bottom shape.
//
// Sizing (P13h): the open tabs FLEX — each rests at a standard width and
// shrinks toward a floor as more open, growing back toward the standard as
// they close, so a normal working set always fits the strip (the CSS carries
// this; no measurement). Past the floor (even shrunk tabs overflow)
// the strip degrades gracefully: it scrolls horizontally AND surfaces an
// overflow menu (the ⋯-sibling chevron glyph, the "reveal more" disclosure,
// distinct from the row-action kebab) listing every open tab so none is ever
// lost off-screen. Whether the strip overflows is the ONE thing this component
// measures — the sanctioned layout-measurement boundary (a ResizeObserver over
// the scroll box), not domain I/O; everything else stays pure props → intent.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { disambiguate } from "../Navigation/disambiguate.js";
import type {
    ActiveTab,
    ConversationId,
    ResourceId,
    ResourceKind,
    ResourceRef,
    ResourceTab,
    TabSet,
} from "../Navigation/types.js";
import { OverflowTooltip } from "../Tooltip/OverflowTooltip.js";
import { TooltipProvider } from "../Tooltip/Tooltip.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./ResourceTabs.css";

export type ResourceTabsProps = {
    // The working set, as the shared vocabulary shapes it — the conversation
    // tabs included (`conversationsOpen` + the `conversation` active variant,
    // composition contract D3/I8). The host owns it and advances it with the
    // Navigation folds; this strip only renders.
    readonly tabs: TabSet;
    // Labels each conversation tab by its id (caller-owned) — a per-id lookup,
    // not one shared string, so multiple open threads each show their own
    // (renameable) title. The caller falls back to a generic label (e.g. "New
    // chat") for a NASCENT thread with no message and no summary yet.
    readonly conversationTitleFor: (id: ConversationId) => string;
    // Labels the Settings singleton tab (caller-owned — the localised noun).
    readonly settingsTitle: string;
    // Labels the Home overview singleton tab (caller-owned — the localised noun).
    readonly homeTitle: string;
    // Labels the Search singleton tab (caller-owned — the localised noun).
    readonly searchTitle: string;
    // The author picked a tab — home, one open resource, the conversation, or
    // Settings. The host applies `activateTab` (or routes) and passes the next
    // TabSet back down.
    readonly onActivate: (tab: ActiveTab) => void;
    // The author asked to close a resource tab. The host runs `closeTab`; on
    // `unsaved` it confirms via ConfirmDiscard before `closeTabDiscarding`.
    readonly onClose: (id: ResourceId) => void;
    // Batch curation from a tab's kebab (same-command — the host runs the
    // `close-other-tabs` / `close-tabs-to-right` frame folds).
    readonly onCloseOthers: (id: ResourceId) => void;
    readonly onCloseToRight: (id: ResourceId) => void;
    // The author asked to close the conversation tab — its own intent
    // (never guarded: the conversation lives server-side).
    readonly onCloseConversation: (id: ConversationId) => void;
    // The author asked to close the Settings tab — its own unguarded intent.
    readonly onCloseSettings: () => void;
    // The author asked to close the Home overview tab — its own unguarded intent.
    readonly onCloseHome: () => void;
    // The author asked to close the Search tab — its own unguarded intent.
    readonly onCloseSearch: () => void;
    // The author asked to close a full-quality report tab — its own unguarded
    // intent, keyed by the material the report is scoped to.
    readonly onCloseQuality: (materialId: ResourceId) => void;
    // The author asked for a new tab — the "+" affordance, the visible twin of
    // ⌘T: every click opens a fresh nascent conversation beside the existing
    // tabs (multiple allowed, no dedup).
    readonly onNewTab: () => void;
    // The pane's ONE weak GLANCE (preview) tab, or null. Rendered italic as the
    // LAST resource tab, before the conversation/settings singletons
    // (nav-history §3a). A bare ref (no `dirty` — a glance is never dirty).
    readonly preview?: ResourceRef | null;
    // Single-click the glance re-activates it (it is already the glance).
    readonly onActivatePreview?: () => void;
    // Double-click the glance → keep it (VS Code's promote-on-double-click).
    readonly onPromotePreview?: () => void;
    // × or middle-click the glance → drop it (unguarded — never dirty).
    readonly onClosePreview?: () => void;
    readonly className?: string;
};

// A page with a folded corner — a source (reading / evidence).
const sourceGlyph = <Icon name="source" />;

// A pen — a material (authoring).
const materialGlyph = <Icon name="material" />;

// A box — a package (export).
const exportGlyph = <Icon name="export" />;
const fileGlyph = <Icon name="file" />;

// A speech bubble — the conversation's tab (the launcher's assistant glyph).
const conversationGlyph = <Icon name="chat" />;

// A gear — the Settings singleton tab.
const settingsGlyph = <Icon name="gear" />;

// A house — the Home overview singleton tab.
const homeGlyph = <Icon name="home" />;

// A magnifier — the Search singleton tab.
const searchGlyph = <Icon name="search" />;

// A shield — a full-quality report tab (the material's policy evaluation). Reads
// as "checked / guarded quality", distinct from every resource-kind glyph.
const qualityGlyph = <Icon name="shield" />;

// A plus — the trailing "+" new-tab affordance ONLY (the visible twin of ⌘T).
const newTabGlyph = <Icon name="plus" />;

const closeGlyph = <Icon name="close" size="xs" />;

// The per-tab curation kebab (the row-action glyph — Close others / to the
// right). Distinct from the strip's chevron overflow (which reveals hidden tabs).
const moreGlyph = <Icon name="overflow" size="xs" />;

// The overflow-menu trigger — a downward chevron ("reveal the rest"), kept
// deliberately distinct from the row-action kebab (`overflow`): one concept,
// one form (the kebab acts on a row; this discloses the hidden tabs).
const overflowGlyph = <Icon name="chevron-down" />;

// The kind glyph — a total match over the shared discriminant, so a new
// resource kind fails to compile until its glyph is chosen.
const kindGlyph = (kind: ResourceKind): ReactNode => {
    switch (kind) {
        case "source":
            return sourceGlyph;
        case "material":
            return materialGlyph;
        case "export":
            return exportGlyph;
        case "file":
            return fileGlyph;
        default:
            return exhaustSwitchCase(kind);
    }
};

const isActive = (active: ActiveTab, tab: ResourceTab): boolean =>
    active.type === "resource" && active.id === tab.resource.id;

// One resource tab: the activating button (glyph + title + dirty channel) and
// its sibling close button. Siblings, never nested — see the header's a11y
// note. The active fill + ember cap span the pair via the wrapper's data-active.
const TabItem = ({
    tab,
    active,
    ordinal,
    onActivate,
    onClose,
    onCloseOthers,
    onCloseToRight,
}: {
    readonly tab: ResourceTab;
    readonly active: boolean;
    // The disambiguating ` · N` for a same-name sibling in the open set
    // (P18a), or undefined when this title is unique in the strip.
    readonly ordinal: number | undefined;
    readonly onActivate: (t: ActiveTab) => void;
    readonly onClose: (id: ResourceId) => void;
    // Batch-curation intents (same-command — the host runs the frame folds).
    readonly onCloseOthers: (id: ResourceId) => void;
    readonly onCloseToRight: (id: ResourceId) => void;
}) => {
    const { t } = useLingui();
    // The tab's curation menu (VS Code / browser convention): Close others,
    // Close to the right. "Close" itself stays the always-visible × affordance.
    const curationItems: readonly MenuItem[] = [
        {
            _tag: "item",
            label: t`Close others`,
            onSelect: () => onCloseOthers(tab.resource.id),
        },
        {
            _tag: "item",
            label: t`Close tabs to the right`,
            onSelect: () => onCloseToRight(tab.resource.id),
        },
    ];
    return (
        <div
            className="bilda-resource-tabs__tab"
            data-active={active ? "true" : undefined}
        >
            <button
                type="button"
                className="bilda-resource-tabs__activate"
                aria-current={active ? "page" : undefined}
                onClick={() =>
                    onActivate({ type: "resource", id: tab.resource.id })
                }
            >
                <span className="bilda-resource-tabs__glyph" aria-hidden="true">
                    {kindGlyph(tab.resource.type)}
                </span>
                <OverflowTooltip content={tab.resource.title}>
                    {(measureRef) => (
                        <span
                            ref={measureRef}
                            className="bilda-resource-tabs__title"
                        >
                            {tab.resource.title}
                            {ordinal !== undefined ? (
                                <span className="bilda-resource-tabs__ordinal">
                                    {` · ${ordinal}`}
                                </span>
                            ) : null}
                        </span>
                    )}
                </OverflowTooltip>
                {tab.dirty ? (
                    // The dot is the visible channel; the hidden text is the
                    // spoken one — dirty never rides on colour alone.
                    <>
                        <span
                            className="bilda-resource-tabs__dirty"
                            aria-hidden="true"
                        />
                        <VisuallyHidden>
                            <Trans>unsaved changes</Trans>
                        </VisuallyHidden>
                    </>
                ) : null}
            </button>
            <Menu
                align="end"
                trigger={
                    <IconButton
                        label={t`More actions for ${tab.resource.title}`}
                        variant="ghost"
                        size="sm"
                        className="bilda-resource-tabs__more"
                    >
                        {moreGlyph}
                    </IconButton>
                }
                items={curationItems}
            />
            <IconButton
                label={t`Close ${tab.resource.title}`}
                variant="ghost"
                size="sm"
                className="bilda-resource-tabs__close"
                onClick={() => onClose(tab.resource.id)}
            >
                {closeGlyph}
            </IconButton>
        </div>
    );
};

export const ResourceTabs = ({
    tabs,
    conversationTitleFor,
    settingsTitle,
    homeTitle,
    searchTitle,
    onActivate,
    onClose,
    onCloseOthers,
    onCloseToRight,
    onCloseConversation,
    onCloseSettings,
    onCloseHome,
    onCloseSearch,
    onCloseQuality,
    onNewTab,
    preview = null,
    onActivatePreview,
    onPromotePreview,
    onClosePreview,
    className,
}: ResourceTabsProps) => {
    const { t } = useLingui();
    // Same-name open tabs get a dimmed ` · N` ordinal (P18a) — the working-set
    // scope is the open strip, so collisions are computed across `open`.
    const ordinals = disambiguate(tabs.open.map((tab) => tab.resource));

    // The ONE measurement: does the flexing strip overflow even at the tab
    // floor? A ResizeObserver watches the scroll box, and the effect re-runs
    // when the open set changes (a new tab grows the scroll width without
    // resizing the box). No layout is written back — only a boolean that
    // arms the overflow menu, so the pure props → intent contract holds.
    const stripRef = useRef<HTMLDivElement | null>(null);
    const [overflowing, setOverflowing] = useState(false);
    const qualitySignature = tabs.qualityOpen
        .map((q) => q.materialId)
        .join(" ");
    const openSignature = tabs.open.map((tab) => tab.resource.id).join(" ");
    const conversationSignature = tabs.conversationsOpen.join(" ");
    // biome-ignore lint/correctness/useExhaustiveDependencies: the signature + conversation flag ARE the re-measure trigger (the body reads neither directly — it reads the live DOM box).
    useEffect(() => {
        const el = stripRef.current;
        if (el === null) return;
        const measure = () =>
            setOverflowing(el.scrollWidth - el.clientWidth > 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [
        openSignature,
        qualitySignature,
        conversationSignature,
        tabs.settingsOpen,
        tabs.homeOpen,
        tabs.searchOpen,
    ]);

    // The overflow menu's rows — every open resource tab, then the
    // conversation tab, each activating its own tab so a scrolled-out tab is
    // always one click away (not only reachable by dragging the scrollbar).
    const overflowItems: readonly MenuItem[] = [
        ...(tabs.homeOpen
            ? [
                  {
                      _tag: "item",
                      icon: homeGlyph,
                      label: homeTitle,
                      onSelect: () => onActivate({ type: "home" }),
                  } satisfies MenuItem,
              ]
            : []),
        ...(tabs.searchOpen
            ? [
                  {
                      _tag: "item",
                      icon: searchGlyph,
                      label: searchTitle,
                      onSelect: () => onActivate({ type: "search" }),
                  } satisfies MenuItem,
              ]
            : []),
        ...tabs.open.map((tab): MenuItem => {
            const ordinal = ordinals.get(tab.resource.id);
            return {
                _tag: "item",
                icon: kindGlyph(tab.resource.type),
                label:
                    ordinal !== undefined
                        ? `${tab.resource.title} · ${ordinal}`
                        : tab.resource.title,
                onSelect: () =>
                    onActivate({ type: "resource", id: tab.resource.id }),
            };
        }),
        ...tabs.qualityOpen.map(
            (q): MenuItem => ({
                _tag: "item",
                icon: qualityGlyph,
                label: t`Quality: ${q.title}`,
                onSelect: () =>
                    onActivate({ type: "quality", materialId: q.materialId }),
            }),
        ),
        ...tabs.conversationsOpen.map(
            (id): MenuItem => ({
                _tag: "item",
                icon: conversationGlyph,
                label: conversationTitleFor(id),
                onSelect: () => onActivate({ type: "conversation", id }),
            }),
        ),
        ...(tabs.settingsOpen
            ? [
                  {
                      _tag: "item",
                      icon: settingsGlyph,
                      label: settingsTitle,
                      onSelect: () => onActivate({ type: "settings" }),
                  } satisfies MenuItem,
              ]
            : []),
    ];

    return (
        // One TooltipProvider for the strip so the tabs' truncation tooltips
        // share a timer (Radix requires a provider ancestor) — the NavRail
        // pattern.
        <TooltipProvider>
            <nav
                aria-label={t`Open resources`}
                className={`bilda-resource-tabs${className ? ` ${className}` : ""}`}
            >
                <div className="bilda-resource-tabs__strip" ref={stripRef}>
                    {tabs.homeOpen ? (
                        /* The Home overview singleton tab — leads the strip (the
                         * workspace's landing chip). Activates and closes like any
                         * tab; same sibling anatomy as TabItem, per the a11y note
                         * above. Never dirty. */
                        <div
                            className="bilda-resource-tabs__tab"
                            data-active={
                                tabs.active.type === "home" ? "true" : undefined
                            }
                        >
                            <button
                                type="button"
                                className="bilda-resource-tabs__activate"
                                aria-current={
                                    tabs.active.type === "home"
                                        ? "page"
                                        : undefined
                                }
                                onClick={() => onActivate({ type: "home" })}
                            >
                                <span
                                    className="bilda-resource-tabs__glyph"
                                    aria-hidden="true"
                                >
                                    {homeGlyph}
                                </span>
                                <span className="bilda-resource-tabs__title">
                                    {homeTitle}
                                </span>
                            </button>
                            <IconButton
                                label={t`Close ${homeTitle}`}
                                variant="ghost"
                                size="sm"
                                className="bilda-resource-tabs__close"
                                onClick={onCloseHome}
                            >
                                {closeGlyph}
                            </IconButton>
                        </div>
                    ) : null}
                    {tabs.searchOpen ? (
                        /* The Search singleton tab — follows Home in the strip.
                         * Activates and closes like any tab; same sibling anatomy
                         * as TabItem, per the a11y note above. Never dirty. */
                        <div
                            className="bilda-resource-tabs__tab"
                            data-active={
                                tabs.active.type === "search"
                                    ? "true"
                                    : undefined
                            }
                        >
                            <button
                                type="button"
                                className="bilda-resource-tabs__activate"
                                aria-current={
                                    tabs.active.type === "search"
                                        ? "page"
                                        : undefined
                                }
                                onClick={() => onActivate({ type: "search" })}
                            >
                                <span
                                    className="bilda-resource-tabs__glyph"
                                    aria-hidden="true"
                                >
                                    {searchGlyph}
                                </span>
                                <span className="bilda-resource-tabs__title">
                                    {searchTitle}
                                </span>
                            </button>
                            <IconButton
                                label={t`Close ${searchTitle}`}
                                variant="ghost"
                                size="sm"
                                className="bilda-resource-tabs__close"
                                onClick={onCloseSearch}
                            >
                                {closeGlyph}
                            </IconButton>
                        </div>
                    ) : null}
                    {tabs.open.map((tab) => (
                        <TabItem
                            key={tab.resource.id}
                            tab={tab}
                            active={isActive(tabs.active, tab)}
                            ordinal={ordinals.get(tab.resource.id)}
                            onActivate={onActivate}
                            onClose={onClose}
                            onCloseOthers={onCloseOthers}
                            onCloseToRight={onCloseToRight}
                        />
                    ))}
                    {preview !== null ? (
                        /* The GLANCE tab — VS Code's italic preview slot. The
                         * LAST resource tab (before the singletons). Single-click
                         * re-activates it; double-click keeps it; the × (and a
                         * middle-click) drops it. Italic label = "not yet kept". */
                        <div
                            className="bilda-resource-tabs__tab"
                            data-preview="true"
                            data-active={
                                tabs.active.type === "resource" &&
                                tabs.active.id === preview.id
                                    ? "true"
                                    : undefined
                            }
                        >
                            <button
                                type="button"
                                className="bilda-resource-tabs__activate"
                                aria-current={
                                    tabs.active.type === "resource" &&
                                    tabs.active.id === preview.id
                                        ? "page"
                                        : undefined
                                }
                                onClick={() => onActivatePreview?.()}
                                onDoubleClick={() => onPromotePreview?.()}
                                onAuxClick={(e) => {
                                    // Middle-click closes, mirroring a kept tab.
                                    if (e.button === 1) onClosePreview?.();
                                }}
                            >
                                <span
                                    className="bilda-resource-tabs__glyph"
                                    aria-hidden="true"
                                >
                                    {kindGlyph(preview.type)}
                                </span>
                                <OverflowTooltip content={preview.title}>
                                    {(measureRef) => (
                                        <span
                                            ref={measureRef}
                                            className="bilda-resource-tabs__title"
                                        >
                                            {preview.title}
                                        </span>
                                    )}
                                </OverflowTooltip>
                                <VisuallyHidden>
                                    <Trans>preview</Trans>
                                </VisuallyHidden>
                            </button>
                            <IconButton
                                label={t`Close ${preview.title}`}
                                variant="ghost"
                                size="sm"
                                className="bilda-resource-tabs__close"
                                onClick={() => onClosePreview?.()}
                            >
                                {closeGlyph}
                            </IconButton>
                        </div>
                    ) : null}
                    {tabs.qualityOpen.map((q) => {
                        /* A full-quality report tab (owner decision: its own
                         * tab). A per-material COLLECTION member — sits after the
                         * resources it reports on, before the singletons.
                         * Activates and closes like any tab; same sibling anatomy
                         * as TabItem, per the a11y note above. Never dirty. */
                        const active =
                            tabs.active.type === "quality" &&
                            tabs.active.materialId === q.materialId;
                        const label = t`Quality: ${q.title}`;
                        return (
                            <div
                                key={q.materialId}
                                className="bilda-resource-tabs__tab"
                                data-active={active ? "true" : undefined}
                            >
                                <button
                                    type="button"
                                    className="bilda-resource-tabs__activate"
                                    aria-current={active ? "page" : undefined}
                                    onClick={() =>
                                        onActivate({
                                            type: "quality",
                                            materialId: q.materialId,
                                        })
                                    }
                                >
                                    <span
                                        className="bilda-resource-tabs__glyph"
                                        aria-hidden="true"
                                    >
                                        {qualityGlyph}
                                    </span>
                                    <OverflowTooltip content={label}>
                                        {(measureRef) => (
                                            <span
                                                ref={measureRef}
                                                className="bilda-resource-tabs__title"
                                            >
                                                {label}
                                            </span>
                                        )}
                                    </OverflowTooltip>
                                </button>
                                <IconButton
                                    label={t`Close ${label}`}
                                    variant="ghost"
                                    size="sm"
                                    className="bilda-resource-tabs__close"
                                    onClick={() => onCloseQuality(q.materialId)}
                                >
                                    {closeGlyph}
                                </IconButton>
                            </div>
                        );
                    })}
                    {tabs.conversationsOpen.map((id) => {
                        const active =
                            tabs.active.type === "conversation" &&
                            tabs.active.id === id;
                        const conversationTitle = conversationTitleFor(id);
                        return (
                            /* The conversation tab — a real TabSet member (D3):
                             * activates, cycles and closes like any resource tab,
                             * and sits last in the strip. Same sibling anatomy as
                             * TabItem, per the a11y note above. */
                            <div
                                key={id}
                                className="bilda-resource-tabs__tab"
                                data-active={active ? "true" : undefined}
                            >
                                <button
                                    type="button"
                                    className="bilda-resource-tabs__activate"
                                    aria-current={active ? "page" : undefined}
                                    onClick={() =>
                                        onActivate({ type: "conversation", id })
                                    }
                                >
                                    <span
                                        className="bilda-resource-tabs__glyph"
                                        aria-hidden="true"
                                    >
                                        {conversationGlyph}
                                    </span>
                                    <span className="bilda-resource-tabs__title">
                                        {conversationTitle}
                                    </span>
                                </button>
                                <IconButton
                                    label={t`Close ${conversationTitle}`}
                                    variant="ghost"
                                    size="sm"
                                    className="bilda-resource-tabs__close"
                                    onClick={() => onCloseConversation(id)}
                                >
                                    {closeGlyph}
                                </IconButton>
                            </div>
                        );
                    })}
                    {tabs.settingsOpen ? (
                        /* The Settings singleton tab — sits last (after the
                         * conversation), activates and closes like any tab. Same
                         * sibling anatomy as TabItem, per the a11y note above. */
                        <div
                            className="bilda-resource-tabs__tab"
                            data-active={
                                tabs.active.type === "settings"
                                    ? "true"
                                    : undefined
                            }
                        >
                            <button
                                type="button"
                                className="bilda-resource-tabs__activate"
                                aria-current={
                                    tabs.active.type === "settings"
                                        ? "page"
                                        : undefined
                                }
                                onClick={() => onActivate({ type: "settings" })}
                            >
                                <span
                                    className="bilda-resource-tabs__glyph"
                                    aria-hidden="true"
                                >
                                    {settingsGlyph}
                                </span>
                                <span className="bilda-resource-tabs__title">
                                    {settingsTitle}
                                </span>
                            </button>
                            <IconButton
                                label={t`Close ${settingsTitle}`}
                                variant="ghost"
                                size="sm"
                                className="bilda-resource-tabs__close"
                                onClick={onCloseSettings}
                            >
                                {closeGlyph}
                            </IconButton>
                        </div>
                    ) : null}
                </div>
                {/* The "+" new-tab affordance — the visible twin of ⌘T. Always
                 * shown: every click opens a fresh nascent conversation beside
                 * the existing ones (multiple allowed, no dedup), so a new tab is
                 * always one click away. Sits after the strip, before overflow. */}
                <IconButton
                    label={t`New tab`}
                    variant="ghost"
                    size="sm"
                    className="bilda-resource-tabs__new"
                    onClick={onNewTab}
                >
                    {newTabGlyph}
                </IconButton>
                {/* The graceful-degradation affordance — appears ONLY when the
                 * shrunk strip still overflows, so a calm working set never shows
                 * chrome it does not need. Reaches every hidden tab by click; the
                 * scroll reaches them too (belt and braces). */}
                {overflowing ? (
                    <div className="bilda-resource-tabs__overflow">
                        <Menu
                            align="end"
                            trigger={
                                <IconButton
                                    label={t`Show all open tabs`}
                                    variant="ghost"
                                    size="sm"
                                >
                                    {overflowGlyph}
                                </IconButton>
                            }
                            items={overflowItems}
                        />
                    </div>
                ) : null}
            </nav>
        </TooltipProvider>
    );
};
